import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import {
  Ingredient,
  IngredientPayload,
  FoodGroup,
  Season,
  Allergen,
  AllergenPresenceType,
} from '../../types/catalog'
import { FormField, Select } from '../../components/ui/FormField'
import { TranslationFields } from '../../components/ui/TranslationFields'
import { Button } from '../../components/ui/Button'
import { tokens } from '../../styles/tokens'

interface Props {
  initial?: Ingredient | null
  foodGroups: FoodGroup[]
  seasons: Season[]
  allergens: Allergen[]
  onChange: (payload: IngredientPayload) => void
}

const CheckGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  max-height: 160px;
  overflow-y: auto;
  padding: 2px;
`

const CheckItem = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$checked ? tokens.purple400 : tokens.purple100)};
  background: ${p => (p.$checked ? tokens.purple50 : 'white')};
  cursor: pointer;
  font-size: 12px;
  color: ${tokens.purple900};
  transition: all 0.1s;

  input {
    accent-color: ${tokens.purple400};
  }
`

const AllergenRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${tokens.purple100};
  border-radius: 8px;
  margin-bottom: 6px;
`

const AllergenName = styled.span`
  flex: 1;
  font-size: 12px;
  color: ${tokens.purple900};
`

const PresenceSelect = styled.select`
  font-size: 11px;
  padding: 3px 6px;
  border: 1px solid ${tokens.purple100};
  border-radius: 6px;
  color: ${tokens.purple600};
  font-family: 'DM Sans', sans-serif;
  background: white;
`

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: #c0436a;
  cursor: pointer;
  font-size: 14px;
  padding: 0 4px;
  line-height: 1;
`

const AddAllergenRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

export function IngredientForm({ initial, foodGroups, seasons, allergens, onChange }: Props) {
  const [foodGroupId, setFoodGroupId] = useState(initial?.foodGroup.id ?? '')
  const [seasonIds, setSeasonIds] = useState<string[]>(initial?.seasons.map(s => s.id) ?? [])
  const [selectedAllergens, setSelectedAllergens] = useState(
    initial?.allergens.map(a => ({
      allergenId: a.allergen.id,
      presenceType: a.presenceType as AllergenPresenceType,
    })) ?? []
  )
  const [addAllergenId, setAddAllergenId] = useState('')
  const [translations, setTranslations] = useState(
    initial?.translations ?? [
      { locale: 'en', name: '' },
      { locale: 'es', name: '' },
    ]
  )

  const toggleSeason = (id: string) => {
    setSeasonIds(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]))
  }

  const addAllergen = () => {
    if (!addAllergenId || selectedAllergens.find(a => a.allergenId === addAllergenId)) return
    setSelectedAllergens(prev => [...prev, { allergenId: addAllergenId, presenceType: 'contains' }])
    setAddAllergenId('')
  }

  const removeAllergen = (allergenId: string) => {
    setSelectedAllergens(prev => prev.filter(a => a.allergenId !== allergenId))
  }

  const updatePresence = (allergenId: string, presenceType: AllergenPresenceType) => {
    setSelectedAllergens(prev =>
      prev.map(a => (a.allergenId === allergenId ? { ...a, presenceType } : a))
    )
  }

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    onChangeRef.current({
      translations,
      foodGroupId,
      nutritionalInfo: null,
      seasonIds,
      allergens: selectedAllergens,
      active: true,
    })
  }, [translations, foodGroupId, seasonIds, selectedAllergens])

  const availableAllergens = allergens.filter(
    a => !selectedAllergens.find(s => s.allergenId === a.id)
  )

  return (
    <>
      <TranslationFields translations={translations} onChange={setTranslations} />

      <FormField label="Food group">
        <Select value={foodGroupId} onChange={e => setFoodGroupId(e.target.value)}>
          <option value="">Select a food group</option>
          {foodGroups.map(fg => (
            <option key={fg.id} value={fg.id}>
              {fg.translations.find(t => t.locale === 'en')?.name ?? fg.code}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Seasons" hint="Select all that apply">
        <CheckGrid>
          {seasons.map(season => {
            const checked = seasonIds.includes(season.id)
            return (
              <CheckItem key={season.id} $checked={checked}>
                <input type="checkbox" checked={checked} onChange={() => toggleSeason(season.id)} />
                {season.translations.find(t => t.locale === 'en')?.name ?? season.code}
              </CheckItem>
            )
          })}
        </CheckGrid>
      </FormField>

      <FormField label="Allergens">
        {selectedAllergens.map(sa => {
          const allergen = allergens.find(a => a.id === sa.allergenId)
          if (!allergen) return null
          return (
            <AllergenRow key={sa.allergenId}>
              <AllergenName>
                {allergen.translations.find(t => t.locale === 'en')?.name ?? allergen.code}
              </AllergenName>
              <PresenceSelect
                value={sa.presenceType}
                onChange={e =>
                  updatePresence(sa.allergenId, e.target.value as AllergenPresenceType)
                }
              >
                <option value="contains">Contains</option>
                <option value="may_contain">May contain</option>
              </PresenceSelect>
              <RemoveBtn type="button" onClick={() => removeAllergen(sa.allergenId)}>
                ✕
              </RemoveBtn>
            </AllergenRow>
          )
        })}

        {availableAllergens.length > 0 && (
          <AddAllergenRow>
            <Select
              value={addAllergenId}
              onChange={e => setAddAllergenId(e.target.value)}
              style={{ flex: 1 }}
            >
              <option value="">Add an allergen…</option>
              {availableAllergens.map(a => (
                <option key={a.id} value={a.id}>
                  {a.translations.find(t => t.locale === 'en')?.name ?? a.code}
                </option>
              ))}
            </Select>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addAllergen}
              disabled={!addAllergenId}
            >
              Add
            </Button>
          </AddAllergenRow>
        )}
      </FormField>
    </>
  )
}
